import * as icons from '@mui/icons-material';
import type { PaperProps } from '@mui/material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useList } from '@refinedev/core';
import type { PropsWithChildren } from 'react';
import { forwardRef, useMemo } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useController, useFormContext, type RegisterOptions } from 'react-hook-form';
import type { DocumentId, MagazineArticle, Series } from '../../types/index.ts';
import { DocumentHead } from '../../types/index.ts';
import { SortableList } from '../SortableList.tsx';
import { ReadOnlyField } from './ReadOnlyField.tsx';

type SortableListFieldProps = {
  name: string;
  rules?: RegisterOptions;
  label: string;
};

export const MagazineArticleListField = forwardRef<HTMLDivElement, SortableListFieldProps>(
  ({ name, label, rules, ...props }, ref) => {
    const { control, watch } = useFormContext<FieldValues>();
    const { field, fieldState } = useController({
      name,
      control,
      rules,
      defaultValue: [],
    });

    const seriesList: Array<Series> = watch('series');
    const magazineArticles: Array<MagazineArticle> = field.value;
    const onChangeMagazineArticles = field.onChange;

    const { refetch: fetchMagazineArticles } = useList<DocumentHead>({
      resource: 'contents/documents',
      queryOptions: {
        // 最初のfetchは行わず、refetchでのみ取得する
        enabled: false,
        select({ data, total }) {
          return {
            data: data.map((row) => DocumentHead.parse(row)),
            total,
          };
        },
      },
      pagination: {
        pageSize: 100,
      },
      sorters: [
        {
          // 雑誌は表紙から順にCMSに登録されるとのことなので、作成日が古い順に並べる
          field: 'createdAt',
          order: 'asc',
        },
      ],
      filters: [
        { field: 'type', operator: 'eq', value: 'magazine_article' },
        {
          // この時点ではどれが雑誌号のシリーズに当たるか不明なので、全てのシリーズIDで問い合わせる
          field: 'series',
          operator: 'in',
          value: seriesList.map((series) => series.name),
        },
      ],
    });

    const fetchCorrespondingMagazineArticles = async () => {
      if (seriesList.length > 0) {
        const { data } = await fetchMagazineArticles();
        if (typeof data?.total === 'number' && data.total > 100) {
          alert('対応する雑誌記事が100件を超えるため、全てを取得できませんでした。');
        }
        onChangeMagazineArticles(data?.data);
      } else {
        onChangeMagazineArticles([]);
      }
    };

    const itemMap = useMemo(
      () => new Map(magazineArticles.map((item) => [item.id, item])),
      [magazineArticles],
    );

    return (
      <ReadOnlyField
        label={label}
        margin="normal"
        variant="outlined"
        error={!!fieldState.error}
        helperText={
          fieldState.error?.message ?? '順番をドラッグ＆ドロップで入れ替えることができます'
        }
      >
        <Box sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
          <SortableList
            sortStrategy="verticalList"
            ids={magazineArticles.map((item) => item.id) ?? []}
            onChange={(ids) => {
              field.onChange(ids.map((id) => itemMap.get(id as DocumentId)));
            }}
            renderItem={(id) => {
              const item = itemMap.get(id as DocumentId);

              return (
                <ArticleCard
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="h6">{item?.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item?.publishedAt ? item.publishedAt.toISOString().slice(0, 10) : '(未公開)'}
                      ・{item?.authors.map((author) => author.name).join(' / ') || '(著者未設定)'}
                    </Typography>
                  </Stack>
                </ArticleCard>
              );
            }}
          />
        </Box>
        <ArticleCard
          sx={{
            marginTop: 1,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            backgroundColor: '#00000010',
          }}
          onClick={fetchCorrespondingMagazineArticles}
        >
          <icons.SyncOutlined />
          <Typography variant="body2" color="text.secondary">
            シリーズに紐づく雑誌記事（作成日順）を取得する
          </Typography>
        </ArticleCard>
      </ReadOnlyField>
    );
  },
);

const ArticleCard = ({ children, ...props }: PropsWithChildren<PaperProps>) => {
  return (
    <Paper
      {...props}
      variant="outlined"
      sx={{
        ...props.sx,
        gap: 1,
        p: 2,
      }}
    >
      {children}
    </Paper>
  );
};
